import { FunctionComponent, useContext, useEffect, useRef, useState } from 'react';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';

import { CookieKeys } from 'types/cookies';
import { Option } from 'types/options';
import ListCollection from 'types/redux/ListCollection';

import Product, {
    genderOptions,
    Genders,
} from 'models/Product';
import ProductCategory from 'models/ProductCategory';

import { LIST_MARKUP } from 'consts/redux';
import { PRODUCTS } from 'consts/sections';
import { getOptionValue } from 'utils/option';
import { getProvider } from 'services/Cookies';

import { list as listProducts } from 'requests/products';

import DeviceContext from 'components/context/Device';
import LocaleContext from 'components/context/Locale';
import Button, { ButtonElements } from 'components/layout/Button';
import LayoutContainer from 'components/layout/LayoutContainer';
import OptionSelector from 'components/layout/OptionSelector';
import SpinnerNew, { Positions } from 'components/layout/SpinnerNew';
import ProductCategorySelector from 'components/modules/public/layout/ProductCategorySelector';
import OfferBoxProduct from 'components/modules/public/products/OfferBoxProduct';

import StyledComponent from './styles';
import { Props } from './types';

const MobileSlider = dynamic(() => import('components/layout/MobileSlider'));

const PublicSectionPackagesVariantDefault: FunctionComponent<Props> = ({
    translations,
    initialProductList,
    initialLocationId,
    locationId,
    sectionSettings,
    visibility,
    isCheckButtonVisible,
}) => {
    const router = useRouter();
    const cookieGender = getProvider().get(CookieKeys.Gender);
    const isMounted = useRef<boolean>(false);
    const localeContext = useContext(LocaleContext);
    const deviceContext = useContext(DeviceContext);
    const [selectedGender, setSelectedGender]: [Option<Genders>, Function] = useState(genderOptions.find((genderOption) => getOptionValue(genderOption) === (sectionSettings?.filters?.gender || cookieGender || Genders.Female)));
    const [selectedCategory, setSelectedCategory] = useState<Option<ProductCategory>>();
    const [productsList, setProductsList]: [ListCollection<Product>, Function] = useState(initialProductList || { ...LIST_MARKUP });
    const { headlineEpilation, notFound, buttonLabel, buttonLink } = translations;
    const blockSectionProductsTranslations: any = localeContext?.translations?.blockSectionProducts;
    const copy = localeContext?.translations?.blockSectionPackages?.copy;

    const currentHeadline = headlineEpilation;

    const specificBodyPartId = sectionSettings?.filters?.bodyPartGroupId?.value && sectionSettings?.filters?.bodyPartGroupId?.value !== 'Wszystkie' && sectionSettings?.filters?.bodyPartGroupId?.value !== 'All';

    useEffect(() => {
        if (isMounted.current) {
            loadProducts(false);
            clearPathIfPossible();
            setCookieValues();
        } else {
            isMounted.current = true;
        }

        return () => isMounted.current = false;
    }, [selectedGender]);

    useEffect(() => {
        let cancel = false;

        if (!initialProductList?.elements?.length) {
            loadProducts(cancel);
        }
        return () => {
            cancel = true;
        };
    }, [initialProductList, selectedGender, selectedCategory]);

    const setCookieValues = () => {
        getProvider().set(CookieKeys.Gender, getOptionValue(selectedGender));
        router.replace(router.asPath, undefined, { shallow: true });
    };

    const loadProducts = async (cancel): Promise<any> => {
        const isMobile = deviceContext?.isMobile;

        try {
            setProductsList({
                ...LIST_MARKUP,
                isLoading: true,
                isLoaded: false,
            });
            await listProducts({
                page: 1,
                perPage: isMobile ? 3 : (sectionSettings?.filters?.perPage || 6),
                locationId: sectionSettings?.filters?.locationId || initialLocationId,
                bodyPartGroupId: specificBodyPartId ? sectionSettings?.filters?.bodyPartGroupId?.value : undefined,
                gender: getOptionValue(selectedGender),
                categoryId: getOptionValue(selectedCategory)?.id,
                visibility: sectionSettings?.filters?.visibility || visibility,
                type: sectionSettings?.filters?.type || null,
            })
                .then(response => {
                    if (cancel) return;
                    setProductsList({
                        ...LIST_MARKUP,
                        ...response,
                    });
                });
        } catch (error) {
            console.error(error);
            setProductsList({
                ...LIST_MARKUP,
                isLoading: false,
                isLoaded: false,
                error: true,
            });
        }
    };

    const clearPathIfPossible = () => {
        if (router.asPath.includes(`section=${PRODUCTS}`)) {
            return router.push(router.pathname, router.pathname, { shallow: true });
        }
    };

    const productItems = productsList?.elements.map(element => (
        <OfferBoxProduct
            key={element?.id}
            locationId={locationId}
            isBuyNowDisabled={sectionSettings?.filters?.isBuyNowDisabled}
            isAddToCartDisabled={sectionSettings?.filters?.isAddToCartDisabled}
            isCheckButtonVisible={isCheckButtonVisible}
            product={element}
            section={PRODUCTS}
            {...copy}
        />
    ));

    return (
        <StyledComponent className="public-section-packages-variant-default">
            <LayoutContainer>
                {(sectionSettings?.filters?.isCategorySwitchEnabled || sectionSettings?.filters?.isGenderSwitchEnabled) && (
                    <div className="options-selector">
                        {sectionSettings?.filters?.isGenderSwitchEnabled && (
                            <OptionSelector
                                options={genderOptions}
                                selectedOption={selectedGender}
                                onSelect={(nextGender: Option<Genders>) => setSelectedGender(nextGender)}
                            />
                        )}
                        {sectionSettings?.filters?.isCategorySwitchEnabled && (
                            <ProductCategorySelector
                                selectedOption={selectedCategory}
                                onSelect={(nextCategory) => setSelectedCategory(nextCategory)}
                            />
                        )}
                    </div>
                )}
                {(currentHeadline || translations?.headline) && (
                    <div
                        className="headline"
                        dangerouslySetInnerHTML={{ __html: currentHeadline || translations?.headline }}
                    />
                )}
                <div className="offer-boxes">
                    {productsList?.isLoading && (
                        <div className="offer-boxes__loader-container">
                            <SpinnerNew position={Positions.Relative} />
                        </div>
                    )}
                    {(productsList?.elements.length === 0 && productsList?.isLoaded) && (
                        <div
                            className="offer-boxes__not-found"
                            dangerouslySetInnerHTML={{ __html: notFound || blockSectionProductsTranslations?.notFound?.titleUnisex }}
                        />
                    )}
                    {(productsList?.elements.length > 0) && productsList?.isLoaded && (
                        <>
                            {!deviceContext?.isMobile ? (
                                <div className="offer-boxes-desktop">
                                    {productItems}
                                </div>
                            ) : (
                                <MobileSlider
                                    showNormalDots={true}
                                    sliderProps={{
                                        infinite: false,
                                        initialSlide: 0,
                                    }}
                                >
                                    {productItems}
                                </MobileSlider>
                            )}
                        </>
                    )}
                </div>
                {buttonLabel && (
                    <div className="control">
                        <Button
                            element={ButtonElements.Anchor}
                            href={buttonLink}
                        >
                            {buttonLabel}
                        </Button>
                    </div>
                )}
            </LayoutContainer>
        </StyledComponent>
    );
};

export default PublicSectionPackagesVariantDefault;
